import { NETWORK_BRANCH } from './signalThresholds';

/**
 * GT-306 / TT-1377's student-level bad-network rule, applied to the tutor activities the login
 * read already returned (API.saveUserData -> userData.tutorHistorySignals).
 *
 * The rule, from Hriday's TT-1377 brief (Aug 21–Sep 4 study window):
 *
 *   - A SESSION is high-RTT when its send->ack p95 is >= highRttP95Ms (3s).
 *   - A DAY is high-RTT when any session on that calendar day was high-RTT. One is enough.
 *   - The student is bad-network when they have >= minHighRttDays (2) DISTINCT high-RTT calendar
 *     days AND >= minHighRttSessionShare (50%) of their counted sessions were high-RTT.
 *
 * Both halves are required. The brief is explicit about why: on the 2+-day gate alone Click goes
 * from 24 to 245 students, because its students see a slow session on repeated days but only on
 * about 1 in 5 of their tutors. The days do not have to be consecutive, and the share is of every
 * counted session in the window, not of the sessions on one day.
 *
 * badCount (TT-1257's finished-story episode counter) is NOT the trigger. It is written at story
 * end only, so it cannot see a quit, and the brief keeps it as a sanity check. It is summarised
 * here for the log and read by nothing else.
 *
 * LAUNCH-ONLY, BY DESIGN. Unlike the audio half, which re-classifies after every finished tutor
 * activity from a live stash, this rule is computed once from the rows in hand at login and never
 * from the activity that just ended. The audio rule is about the mic in front of the student right
 * now; this one is about whether the network has been chronically slow across days, and a single
 * fresh session cannot change that answer. So nothing in ReaderManager feeds this module.
 *
 * Pure: the thresholds are an argument, so the suite injects numbers and nothing here restates the
 * shipped values.
 */

function toFiniteOrNull(value) {
  return Number.isFinite(value) ? value : null;
}

/**
 * The session's send->ack p95 in ms, from the per-phrase rows TT-1256 persists, or null when no
 * phrase on the row measured one (an activity quit before its first phrase dumped, or a payload
 * written by a client older than #5972).
 *
 * MAX OVER PHRASES. The payload carries per-phrase p95s, not the samples, so the study's "session
 * p95" is reconstructed from them, and this is how TT-1377 did it (Hriday, GT-306, 2026-10-06):
 * a session is as slow as its slowest phrase's tail. Not a percentile of percentiles.
 */
export function sessionAckP95Ms(phrases) {
  if (!Array.isArray(phrases)) {
    return null;
  }
  const measured = phrases
    .map((phrase) => phrase?.ackP95Ms)
    .filter((value) => Number.isFinite(value));
  return measured.length ? Math.max(...measured) : null;
}

/**
 * The calendar day a timestamp falls on, in the device's local time — i.e. the student's own
 * school day, which is the clock the study used (district-local, not Central; that is how Mellon
 * and Click, both in South Africa, were read). YYYY-MM-DD, or null for an unparseable input.
 */
export function localCalendarDay(timestamp) {
  const ms = Date.parse(timestamp);
  if (!Number.isFinite(ms)) {
    return null;
  }
  const date = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * One stashed login row -> one counted session, or null when the row cannot be counted.
 *
 * A row is dropped when its createdAt does not parse (no day to put it on) or when no phrase on it
 * measured an ack. The latter is the brief's own denominator: "sessions with an ack dump". An
 * activity that never got as far as a phrase says nothing about the network and must not dilute
 * the share.
 *
 * NO DATE BOUND, BY DECISION. The window is whatever the login read returned -- the 10 newest
 * non-archived tutor activities -- and the rule is applied to exactly that (Hriday, GT-306,
 * 2026-10-06: students take up to about 5 activities a day, so 10 rows is at least the last two
 * days of use). The audio signal's badSignalMaxSignalAgeDays does not apply here; adding one
 * would be a second window nobody measured.
 */
function toSession(row) {
  const payload = row?.audioNetworkSignals;
  const measuredAt = row?.createdAt;
  if (!Number.isFinite(Date.parse(measuredAt))) {
    return null;
  }
  const p95Ms = sessionAckP95Ms(payload?.phrases);
  if (p95Ms === null) {
    return null;
  }
  return {
    measuredAt,
    day: localCalendarDay(measuredAt),
    sessionP95Ms: p95Ms,
    badCount: toFiniteOrNull(payload?.badCount),
    activityStatus: typeof row?.activityStatus === 'string' ? row.activityStatus : null,
  };
}

/** The counted sessions behind a verdict, in the order the rows arrived. Exported for the log and the suite. */
export function normalizeHistoryRows(rows) {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map(toSession).filter(Boolean);
}

/**
 * The rule. Returns the tri-state verdict the policy reads under hadBadNetwork, the branch map
 * the log records beside TT-1230's audio branches, and the summary that makes a row diagnosable
 * without the source open: how many sessions counted, how many were slow, which days.
 *
 * null — unknown, not bad — when there are no counted sessions, no rows at all, or a threshold is
 * not a finite number. Only a real evaluation says false.
 *
 * @param {Array|null} rows  userData.tutorHistorySignals as API.saveUserData stashes it
 * @param {{highRttP95Ms:number, minHighRttDays:number, minHighRttSessionShare:number, badNetworkCount:number}} networkThresholds
 */
export function classifyNetworkHistory(rows, networkThresholds) {
  const sessions = normalizeHistoryRows(rows);
  const highRttP95Ms = networkThresholds?.highRttP95Ms;
  const minHighRttDays = networkThresholds?.minHighRttDays;
  const minShare = networkThresholds?.minHighRttSessionShare;
  const badCountFloor = networkThresholds?.badNetworkCount;

  const thresholdsUsable = [highRttP95Ms, minHighRttDays, minShare].every((v) => Number.isFinite(v));

  const highRttSessions = thresholdsUsable ? sessions.filter((s) => s.sessionP95Ms >= highRttP95Ms) : [];
  const highRttDays = [...new Set(highRttSessions.map((s) => s.day).filter(Boolean))].sort();
  const sessionCount = sessions.length;
  const highRttSessionCount = highRttSessions.length;
  const highRttShare = sessionCount > 0 ? highRttSessionCount / sessionCount : null;

  let verdict = null;
  if (thresholdsUsable && sessionCount > 0) {
    verdict = highRttDays.length >= minHighRttDays && highRttShare >= minShare;
  }

  const sessionsWithBadCount = sessions.filter((s) => s.badCount !== null);
  const badCountSanity = {
    sessionsMeasured: sessionsWithBadCount.length,
    sessionsWithBadCount: sessionsWithBadCount.filter((s) => s.badCount >= 1).length,
    sessionsAtOrOverFloor: Number.isFinite(badCountFloor)
      ? sessionsWithBadCount.filter((s) => s.badCount >= badCountFloor).length
      : null,
  };

  return {
    hadBadNetwork: verdict,
    networkBranches: { [NETWORK_BRANCH.CHRONIC_HIGH_RTT]: verdict },
    summary: {
      rowCount: Array.isArray(rows) ? rows.length : null,
      sessionCount,
      highRttSessionCount,
      highRttShare,
      highRttDays,
      highRttDayCount: highRttDays.length,
      sessionP95sMs: sessions.map((s) => s.sessionP95Ms),
      badCountSanity,
    },
  };
}
